import type { useInterventionTemplateJsonEditorProps } from "./intervention-template-json-editor.type";
import type { InterventionTemplateNode } from "@/entities/intervention-template";

import { useEffect, useState } from "react";

import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import {
  useAppSaveBar,
  useAppSaveBarHandler,
  useAppSaveBarSync,
} from "@/modules/app-save-bar";

import { INTERVENTION_TEMPLATE_JSON_EDITOR_SCOPE } from "./intervention-template-json-editor.constant";

export const useInterventionTemplateJsonEditor = ({
  template,
}: useInterventionTemplateJsonEditorProps) => {
  const SCOPE = INTERVENTION_TEMPLATE_JSON_EDITOR_SCOPE;

  const { onError } = useApiErrorHandler();
  const { setSaveBar } = useAppSaveBar(SCOPE);
  const { mutate, isPending } = useUpdate("interventionTemplates", template.id);

  const [templates, setTemplates] = useState<InterventionTemplateNode[]>(
    template.templates ?? []
  );

  useEffect(() => {
    setTemplates(template.templates ?? []);
  }, [template]);

  const onSave = () => {
    mutate(
      { templates: templates },
      {
        onSuccess: () => setSaveBar("dirty", false),
        onError,
      }
    );
  };

  const onCancel = () => {
    setTemplates(template.templates ?? []);
    setSaveBar("dirty", false);
  };

  const onSchemaChange = (
    newSchema: InterventionTemplateNode[] | null,
    isRegistrable: boolean
  ) => {
    setTemplates(newSchema ?? []);
    if (isRegistrable) setSaveBar("dirty", true);
  };

  // Sync save bar
  useAppSaveBarHandler(SCOPE, { onSave, onCancel });
  useAppSaveBarSync(SCOPE, "saving", isPending);

  return {
    templates,
    isEditable: template.is_editable ?? false,
    onSchemaChange,
  };
};
